import { ref, watch, useContext, useMeta } from '@nuxtjs/composition-api'
import { getCountryCode } from '~/assets/ts/Locale'

/**
 * Reusable loader for locale + country scoped JSON-LD files.
 * File path convention:
 *   assets/jsonld/<country>/<language>/<baseName>.json
 * - country: getCountryCode()
 * - language: current i18n.locale
 * Accepts multiple base names; existing files are merged into an array of script entries.
 * Missing files are silently ignored.
 */
export function useJsonLd(
  baseNames: string[] | { value: string[] } | (() => string[]),
  options?: { overrideCountry?: string }
) {
  const { app } = useContext()
  const i18n = app.i18n
  // Determine country via helper (two-letter unless override provided)
  const resolvedCountry = options?.overrideCountry || getCountryCode(false)

  const jsonLdList = ref<any[]>([])

  const resolveBaseNames = (): string[] => {
    if (Array.isArray(baseNames)) return baseNames
    if (typeof baseNames === 'function') return baseNames()
    if ((baseNames as any)?.value) return (baseNames as any).value as string[]
    return []
  }

  const load = () => {
    const collected: any[] = []
    resolveBaseNames().forEach((name) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const data = require(`~/assets/jsonld/${resolvedCountry}/${i18n.locale}/${name}.json`)
        collected.push(data)
      } catch (e) {
        // ignore missing
      }
    })
    jsonLdList.value = collected
  }

  load()

  watch(
    () => i18n.locale,
    () => {
      load()
    }
  )

  // Watch dynamic base names if ref or function provided
  if (!Array.isArray(baseNames)) {
    watch(
      () => resolveBaseNames(),
      () => {
        load()
      },
      { deep: true }
    )
  }

  useMeta(() => {
    if (!jsonLdList.value.length) return {}
    return {
      script: jsonLdList.value.map((entry) => ({
        type: 'application/ld+json',
        json: entry,
      })),
    }
  })

  return { jsonLdList }
}
